import { Animation } from '@ionic/core';
import { Component, Host, h, Method, Element, Listen, Event, EventEmitter, Prop, Watch } from '@stencil/core';
import { Components as SyComponents } from '../../components';
import { sidebarCloseAnimation } from './animations/close';
import { sidebarOpenAnimation } from './animations/open';
import { DISABLE_ANIMATION_VALUE } from '../../utils/animations';

/**
 * @slot  - Designed to fill sidebar's content.
 */

@Component({
  tag: 'sy-sidebar',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Sidebar {
  @Element() el: HTMLSySidebarElement;
  private internalVisible: boolean;
  private backdropEl: HTMLSyBackdropElement | undefined;
  private sidebarEl: HTMLElement | undefined;
  private currentAnimation: Animation | undefined;

  /**
   * If true, the backdrop won't be shown.
   */
  @Prop({ reflect: true }) hideBackdrop = false;

  /**
   * If true, the sidebar will be shown.
   */
  @Prop({ mutable: true }) visible = false;

  @Watch('visible')
  visibleWatcher(): Promise<void> | undefined {
    if (this.visible === this.internalVisible) return;

    if (this.visible) {
      return this.show();
    }

    return this.hide();
  }

  /**
   * Placement prop to define the default opening and closing animation according to the edge
   */
  @Prop() placement: 'left' | 'right' = 'left';

  /**
   * Optional custom animation to replace the default and be played when the component starts opening.
   * If 'none', the sidebar won't have open animation.
   */
  @Prop() openAnimation?: ((sidebarElement: HTMLElement, backdropElement: HTMLElement) => Animation) | string;

  /**
   * Optional custom animation to replace the default and be played when the component starts closing.
   * If 'none', the sidebar won't have close animation.
   */
  @Prop() closeAnimation?: ((sidebarElement: HTMLElement, backdropElement: HTMLElement) => Animation) | string;

  /**
   * Disables all animations
   */
  @Prop() disableAnimations = false;

  /**
   * Emitted when the sidebar is shown.
   */
  @Event() sySidebarDidShow: EventEmitter<void>;

  /**
   * Emitted when the sidebar is hidden.
   */
  @Event() sySidebarDidHide: EventEmitter<void>;

  /**
   * Emitted when a sidebar starts to collapse.
   */
  @Event() sySidebarWillHide: EventEmitter<void>;

  /**
   * Emitted when a sidebar starts to expand.
   */
  @Event() sySidebarWillShow: EventEmitter<void>;

  /**
   * Method to hide the sidebar.
   * If 'instant' is true, the sidebar won't have close animation.
   */
  @Method()
  async hide(instant = false): Promise<void> {
    this.internalVisible = false;

    if (this.currentAnimation) {
      this.currentAnimation.stop();
    }

    this.sySidebarWillHide.emit();
    if (!instant && !this.disableAnimations) {
      this.currentAnimation = this.getCloseAnimation();
      if (this.currentAnimation) {
        await this.currentAnimation.play();
      }
    }

    this.visible = false;
    this.sySidebarDidHide.emit();
  }

  /**
   * Method to show the sidebar.
   * If 'instant' is true, the sidebar won't have open animation.
   */
  @Method()
  async show(instant = false): Promise<void> {
    this.internalVisible = true;
    this.visible = true;

    if (this.currentAnimation) {
      this.currentAnimation.stop();
    }

    this.sySidebarWillShow.emit();
    if (!instant && !this.disableAnimations) {
      this.currentAnimation = this.getOpenAnimation();
      if (this.currentAnimation) {
        await this.currentAnimation.play();
      }
    }
    this.sySidebarDidShow.emit();
  }

  @Listen('keydown', { target: 'window' })
  onKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape' && this.visible) {
      this.hide();
    }
  }

  /**
   * Method that toggle the sidebar
   * If 'instant' is true, the sidebar won't have both open and close animations.
   */
  @Method()
  async toggle(instant = false): Promise<void> {
    if (this.visible) {
      return this.hide(instant);
    }
    return this.show(instant);
  }

  private getOpenAnimation() {
    if (typeof this.openAnimation === 'string') {
      if (this.openAnimation === DISABLE_ANIMATION_VALUE) {
        return;
      }
      if (this.sidebarEl && this.backdropEl) {
        return sidebarOpenAnimation(this.sidebarEl, this.backdropEl, this.placement);
      }
      return;
    }
    const animationBuilder = this.openAnimation || sidebarOpenAnimation;
    if (this.sidebarEl && this.backdropEl) {
      return animationBuilder(this.sidebarEl, this.backdropEl, this.placement);
    }
  }

  private getCloseAnimation() {
    if (typeof this.closeAnimation === 'string') {
      if (this.closeAnimation === DISABLE_ANIMATION_VALUE) {
        return;
      }
      if (this.sidebarEl && this.backdropEl) {
        return sidebarCloseAnimation(this.sidebarEl, this.backdropEl, this.placement);
      }
      return;
    }
    const animationBuilder = this.closeAnimation || sidebarCloseAnimation;
    if (this.sidebarEl && this.backdropEl) {
      return animationBuilder(this.sidebarEl, this.backdropEl, this.placement);
    }
  }

  private getBackdroprAttributes(): Partial<SyComponents.SyBackdrop> {
    const attributes: Partial<SyComponents.SyBackdrop> = this.visible ? {} : { hidden: true };

    return attributes;
  }

  render() {
    return (
      <Host>
        <sy-backdrop
          ref={(el) => {
            this.backdropEl = el;
          }}
          {...this.getBackdroprAttributes()}
          onClick={() => this.hide()}
        ></sy-backdrop>
        <div
          ref={(el) => {
            this.sidebarEl = el;
          }}
          class="sy-sidebar"
          style={{ visibility: this.visible ? '' : 'hidden' }}
        >
          <slot></slot>
        </div>
      </Host>
    );
  }
}
